import React from 'react';
import { useTranslation } from 'react-i18next';
import { UseFormReturn } from 'react-hook-form';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Shield, KeyRound, Users, Building2, MapPin } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  usePropertiesDropdownQuery,
  useRoleOptions,
} from '@/hooks/queries/useDropdowns';
import type { CreateUserFormData } from '@/schema/user';

interface UserRoleAccessProps {
  form: UseFormReturn<CreateUserFormData>;
}

export function UserRoleAccess({ form }: UserRoleAccessProps) {
  const { t, i18n } = useTranslation();
  const isRTL = React.useMemo(() => i18n.language === 'ar', [i18n.language]);

  // Get properties and roles for dropdowns
  const { data: properties = [], isLoading: propertiesLoading } =
    usePropertiesDropdownQuery();
  const { options: roleOptions, isLoading: rolesLoading } = useRoleOptions();

  return (
    <Card>
      <CardHeader dir={isRTL ? 'ltr' : 'rtl'}>
        <CardTitle className={cn('flex items-center gap-2')}>
          <Shield className="w-5 h-5" />
          {t('users.create.roleAccess.title')}
        </CardTitle>
        <CardDescription>
          {t('users.create.roleAccess.description')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <FormField
          control={form.control}
          name="role"
          render={({ field }) => (
            <FormItem>
              <FormLabel className={isRTL ? 'text-right' : 'text-left'}>
                {t('users.create.fields.userRole')}
              </FormLabel>
              <Select onValueChange={field.onChange} value={field.value || ''}>
                <FormControl>
                  <SelectTrigger
                    className={cn(isRTL ? 'text-right' : 'text-left')}
                    dir={isRTL ? 'rtl' : 'ltr'}
                  >
                    <SelectValue
                      placeholder={t('users.create.placeholders.selectRole')}
                    />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {rolesLoading ? (
                    <SelectItem value="loading" disabled>
                      {t('users.create.loading')}
                    </SelectItem>
                  ) : (
                    roleOptions.map(role => (
                      <SelectItem key={role.value} value={role.value}>
                        <div
                          className={cn(
                            'flex items-center gap-2',
                            isRTL ? 'flex-row-reverse' : 'flex-row'
                          )}
                        >
                          {role.value === 'admin' && (
                            <KeyRound className="w-4 h-4" />
                          )}
                          {role.value === 'Manager' && (
                            <Users className="w-4 h-4" />
                          )}
                          {role.value === 'Auditor' && (
                            <Building2 className="w-4 h-4" />
                          )}
                          {role.value === 'Department Head' && (
                            <MapPin className="w-4 h-4" />
                          )}
                          {role.value === 'Viewer' && (
                            <Users className="w-4 h-4" />
                          )}
                          {role.label}
                        </div>
                      </SelectItem>
                    ))
                  )}
                </SelectContent>
              </Select>
              <FormMessage className={isRTL ? 'text-right' : 'text-left'} />
            </FormItem>
          )}
        />

        {/* Property Assignment - Only show for manager role */}
        {form.watch('role') === 'manager' && (
          <div className="space-y-4 p-4 bg-muted/30 rounded-lg border">
            <h3
              className={cn(
                'font-medium text-sm',
                isRTL ? 'text-right' : 'text-left'
              )}
            >
              {t('users.create.propertyAssignment.title')}
            </h3>

            <FormField
              control={form.control}
              name="property_id"
              render={({ field }) => (
              <FormItem>
                <FormLabel className={isRTL ? 'text-right' : 'text-left'}>
                  {t('users.create.propertyAssignment.assignedProperty')}
                </FormLabel>
                <Select
                  onValueChange={value =>
                    field.onChange(value === 'all' ? undefined : Number(value))
                  }
                  value={field.value?.toString() || 'all'}
                >
                  <FormControl>
                    <SelectTrigger
                      className={cn(isRTL ? 'text-right' : 'text-left')}
                      dir={isRTL ? 'rtl' : 'ltr'}
                    >
                      <SelectValue
                        placeholder={t(
                          'users.create.placeholders.selectProperty'
                        )}
                      />
                    </SelectTrigger>
                  </FormControl>
                  <SelectContent>
                    {propertiesLoading ? (
                      <SelectItem value="loading" disabled>
                        {t('users.create.loading')}
                      </SelectItem>
                    ) : (
                      <>
                        <SelectItem value="all">
                          <div
                            className={cn(
                              'flex items-center gap-2',
                              isRTL ? 'flex-row-reverse' : 'flex-row'
                            )}
                          >
                            <Building2 className="w-4 h-4" />
                            {t('users.create.propertyAssignment.allProperties')}
                          </div>
                        </SelectItem>
                        {properties.map(property => (
                          <SelectItem
                            key={property.id}
                            value={property.id.toString()}
                          >
                            <div
                              className={cn(
                                'flex items-center gap-2',
                                isRTL ? 'flex-row-reverse' : 'flex-row'
                              )}
                            >
                              <Building2 className="w-4 h-4" />
                              <div className="flex flex-col">
                                <span className="font-medium">
                                  {property.label}
                                </span>
                                <span className="text-xs text-muted-foreground">
                                  Property ID: {property.id}
                                </span>
                              </div>
                            </div>
                          </SelectItem>
                        ))}
                      </>
                    )}
                  </SelectContent>
                </Select>
                <FormMessage className={isRTL ? 'text-right' : 'text-left'} />
              </FormItem>
            )}
          />
          </div>
        )}
      </CardContent>
    </Card>
  );
}
